import React, { useState } from 'react';
import { useApp } from '../context/AppContext';

const fmt = (n, lang) => {
  const locale = lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO';
  const num = Math.round(Math.abs(n)).toLocaleString(locale);
  if (lang==='en') return '$' + num;
  if (lang==='sv') return num + ' kr';
  if (lang==='da') return num + ' kr';
  return num + ' kr';
};

const getMonthNames = (lang) => [
  lang==='en'?'January':lang==='sv'?'Januari':lang==='da'?'Januar':'Januar',
  lang==='en'?'February':lang==='sv'?'Februari':lang==='da'?'Februar':'Februar',
  lang==='en'?'March':lang==='sv'?'Mars':lang==='da'?'Marts':'Mars',
  lang==='en'?'April':lang==='sv'?'April':lang==='da'?'April':'April',
  lang==='en'?'May':lang==='sv'?'Maj':lang==='da'?'Maj':'Mai',
  lang==='en'?'June':lang==='sv'?'Juni':lang==='da'?'Juni':'Juni',
  lang==='en'?'July':lang==='sv'?'Juli':lang==='da'?'Juli':'Juli',
  lang==='en'?'August':lang==='sv'?'Augusti':lang==='da'?'August':'August',
  lang==='en'?'September':lang==='sv'?'September':lang==='da'?'September':'September',
  lang==='en'?'October':lang==='sv'?'Oktober':lang==='da'?'Oktober':'Oktober',
  lang==='en'?'November':lang==='sv'?'November':lang==='da'?'November':'November',
  lang==='en'?'December':lang==='sv'?'December':lang==='da'?'December':'December',
];

const getDayNames = (lang) => [
  lang==='en'?'Mon':lang==='sv'?'Mån':lang==='da'?'Man':'Man',
  lang==='en'?'Tue':lang==='sv'?'Tis':lang==='da'?'Tir':'Tir',
  lang==='en'?'Wed':lang==='sv'?'Ons':lang==='da'?'Ons':'Ons',
  lang==='en'?'Thu':lang==='sv'?'Tor':lang==='da'?'Tor':'Tor',
  lang==='en'?'Fri':lang==='sv'?'Fre':lang==='da'?'Fre':'Fre',
  lang==='en'?'Sat':lang==='sv'?'Lör':lang==='da'?'Lør':'Lør',
  lang==='en'?'Sun':lang==='sv'?'Sön':lang==='da'?'Søn':'Søn',
];

export default function TabKalender() {
  const { state } = useApp();
  const lang = state.language || 'en';
  const [selectedDay, setSelectedDay] = useState(null);
  const now = new Date();
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [viewYear, setViewYear] = useState(now.getFullYear());

  const firstDay = new Date(viewYear, viewMonth, 1);
  const lastDay = new Date(viewYear, viewMonth + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();
  const today = now.getDate();
  const isCurrentMonth = viewMonth === now.getMonth() && viewYear === now.getFullYear();

  // Build txByDay
  const txByDay = {};
  (state.transactions || []).filter(tx => !tx.locked).forEach(tx => {
    try {
      const d = new Date(tx.date);
      if (!isNaN(d.getTime()) && d.getMonth() === viewMonth && d.getFullYear() === viewYear) {
        const key = d.getDate();
        if (!txByDay[key]) txByDay[key] = [];
        txByDay[key].push(tx);
      }
    } catch(e) {}
  });

  // Day color logic
  const getDayColor = (day) => {
    const txs = txByDay[day] || [];
    if (txs.length === 0) return null;
    const saved = txs.filter(t => t.amount > 0 || t.cat === 'Sparing').reduce((s,t) => s + Math.abs(t.amount), 0);
    const spent = txs.filter(t => t.amount < 0 && t.cat !== 'Sparing').reduce((s,t) => s + Math.abs(t.amount), 0);
    const budget = state.dailyBudget || 200;
    if (saved >= 250) return '#7C3AED'; // lilla - sparedag
    if (spent === 0 && saved > 0) return '#7C3AED';
    if (spent === 0) return null;
    if (spent <= budget * 0.75) return '#22C97A'; // grønn
    if (spent <= budget) return '#F5C518'; // gul
    return '#FF4D4D'; // rød
  };

  // Stats
  const totalSpent = Object.values(txByDay).flat().filter(t=>t.amount<0&&t.cat!=='Sparing').reduce((s,t)=>s+Math.abs(t.amount),0);
  const totalSaved = Object.values(txByDay).flat().filter(t=>t.amount>0||t.cat==='Sparing').reduce((s,t)=>s+Math.abs(t.amount),0);
  const daysWithData = Object.keys(txByDay).length;
  const avgPerDay = daysWithData > 0 ? Math.round(totalSpent / daysWithData) : 0;
  const savingDays = Object.entries(txByDay).filter(([d,txs]) => {
    const saved = txs.filter(t=>t.amount>0||t.cat==='Sparing').reduce((s,t)=>s+Math.abs(t.amount),0);
    return saved >= 250;
  }).length;

  // Lønnsdag
  const payDay = state.payDay;
  const now2 = new Date();
  let daysUntil = null;
  let totalLeft = null;
  if (payDay && isCurrentMonth) {
    let nextPay = new Date(now2.getFullYear(), now2.getMonth(), parseInt(payDay));
    if (nextPay <= now2) nextPay = new Date(now2.getFullYear(), now2.getMonth()+1, parseInt(payDay));
    daysUntil = Math.max(0, Math.ceil((nextPay - now2) / (1000*60*60*24)));
  }
  if (isCurrentMonth) {
    const spentThisMonth = (state.transactions || [])
      .filter(t => !t.locked && t.amount < 0 && t.cat !== 'Sparing')
      .reduce((s, t) => s + Math.abs(t.amount), 0);
    totalLeft = Math.max(0, (state.income || 0) - spentThisMonth);
  }

  const selectedTxs = selectedDay ? (txByDay[selectedDay] || []) : [];
  const selectedTotal = selectedTxs.filter(t=>t.amount<0&&t.cat!=='Sparing').reduce((s,t)=>s+Math.abs(t.amount),0);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y=>y-1); }
    else setViewMonth(m=>m-1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y=>y+1); }
    else setViewMonth(m=>m+1);
    setSelectedDay(null);
  };

  const daysInMonth = lastDay.getDate();
  const effectivePayDay = payDay ? Math.min(parseInt(payDay), daysInMonth) : null;

  return (
    <div style={{ padding:'1rem 1rem 2rem' }}>

      {/* Title */}
      <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, color:'var(--ink)', marginBottom:4 }}>
        {lang==='en'?'Calendar':lang==='sv'?'Kalender':lang==='da'?'Kalender':'Kalender'}
      </div>

      {/* Stats grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
        {[
          [lang==='en'?'Total spent':lang==='sv'?'Totalt spenderat':lang==='da'?'Totalt brugt':'Totalt brukt', fmt(totalSpent,lang), 'var(--r400)'],
          [lang==='en'?'Total saved':lang==='sv'?'Totalt sparat':lang==='da'?'Totalt sparet':'Totalt spart', fmt(totalSaved,lang), '#22C97A'],
          [lang==='en'?'Daily avg':lang==='sv'?'Dagligt snitt':lang==='da'?'Dagligt snit':'Daglig snitt', fmt(avgPerDay,lang), 'var(--ink)'],
          [lang==='en'?'Best day':lang==='sv'?'Bästa dag':lang==='da'?'Bedste dag':'Beste dag', Object.entries(txByDay).filter(([,txs])=>txs.filter(t=>t.amount<0).length>0).sort((a,b)=>a[1].filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0)-b[1].filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0))[0]?.[0]||'—', '#22C97A'],
          [lang==='en'?'Worst day':lang==='sv'?'Sämsta dag':lang==='da'?'Værste dag':'Verste dag', Object.entries(txByDay).filter(([,txs])=>txs.filter(t=>t.amount<0).length>0).sort((a,b)=>b[1].filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0)-a[1].filter(t=>t.amount<0).reduce((s,t)=>s+Math.abs(t.amount),0))[0]?.[0]||'—', 'var(--r400)'],
          [lang==='en'?'Saving days':lang==='sv'?'Spardagar':lang==='da'?'Sparedage':'Sparedager', `${savingDays} ${lang==='en'?'days':lang==='sv'?'dagar':lang==='da'?'dage':'dager'}`, '#7C3AED'],
        ].map(([l,v,c]) => (
          <div key={l} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'10px 8px', textAlign:'center' }}>
            <div style={{ fontSize:10, color:'var(--ink3)', fontWeight:600, textTransform:'uppercase', marginBottom:4 }}>{l}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:700, color:c }}>{v}</div>
          </div>
        ))}
      </div>

      {/* Payday section */}
      {payDay && isCurrentMonth && (
        <div style={{ background:'linear-gradient(135deg,rgba(34,201,122,0.1),rgba(0,212,255,0.05))', border:'1.5px solid rgba(34,201,122,0.2)', borderRadius:'var(--r-lg)', padding:'14px 16px', marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#22C97A' }}>
                💰 {lang==='en'?'Until payday':lang==='sv'?'Till löning':lang==='da'?'Til lønningsdag':'Til lønningsdag'}
              </div>
              <div style={{ fontSize:11, color:'var(--ink3)', marginTop:2 }}>
                {daysUntil === 0
                  ? (lang==='en'?'Payday today! 🎉':lang==='sv'?'Löningsdag idag! 🎉':lang==='da'?'Lønningsdag i dag! 🎉':'Lønningsdag i dag! 🎉')
                  : `${daysUntil} ${lang==='en'?'days until payday':lang==='sv'?'dagar till löning':lang==='da'?'dage til lønningsdag':'dager til lønningsdag'}`}
              </div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:20, fontWeight:800, color:'var(--ink)' }}>{fmt(Math.max(0, (state.income||0) - (state.expenses||0) - (state.dailySpent||0)), lang)}</div>
              <div style={{ fontSize:10, color:'var(--ink3)' }}>{lang==='en'?'total left':lang==='sv'?'kvar totalt':lang==='da'?'tilbage i alt':'igjen totalt'}</div>
            </div>
          </div>
          <div style={{ height:6, background:'rgba(0,0,0,0.08)', borderRadius:99, overflow:'hidden' }}>
            <div style={{ width:`${Math.min(100,Math.round((now.getDate()/parseInt(payDay))*100))}%`, height:'100%', background:'linear-gradient(90deg,#22C97A,#00D4FF)', borderRadius:99 }}/>
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:12 }}>
        {[
          ['#22C97A', lang==='en'?'Under budget':lang==='sv'?'Under budget':lang==='da'?'Under budget':'Under budsjett'],
          ['#F5C518', lang==='en'?'Near limit':lang==='sv'?'Nära gränsen':lang==='da'?'Nær grænsen':'Nær grensen'],
          ['#FF4D4D', lang==='en'?'Over budget':lang==='sv'?'Över budget':lang==='da'?'Over budget':'Over budsjett'],
          ['#7C3AED', lang==='en'?'Saving day':lang==='sv'?'Spardag':lang==='da'?'Sparedag':'Sparedag'],
          ['#06B6D4', lang==='en'?'Today':lang==='sv'?'Idag':lang==='da'?'I dag':'I dag'],
        ].map(([c,l]) => (
          <div key={l} style={{ display:'flex', alignItems:'center', gap:4, fontSize:11, color:'var(--ink3)' }}>
            <div style={{ width:10, height:10, borderRadius:3, background:c }}/>
            <span>{l}</span>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'16px', marginBottom:12 }}>

        {/* Month nav */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <button onClick={prevMonth} style={{ width:36, height:36, borderRadius:'var(--r-md)', border:'1px solid var(--border)', background:'var(--surface)', cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center' }}>‹</button>
          <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, color:'var(--ink)' }}>
            {getMonthNames(lang)[viewMonth]} {viewYear}
          </div>
          <button onClick={nextMonth} style={{ width:36, height:36, borderRadius:'var(--r-md)', border:'1px solid var(--border)', background:'var(--surface)', cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center' }}>›</button>
        </div>

        {/* Day headers */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:6 }}>
          {getDayNames(lang).map(d => (
            <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:'var(--ink3)', padding:'4px 0' }}>{d}</div>
          ))}
        </div>

        {/* Day cells - BIGGER */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4 }}>
          {Array.from({ length: startOffset }).map((_,i) => <div key={`e${i}`}/>)}
          {Array.from({ length: totalDays }).map((_,i) => {
            const day = i + 1;
            const color = getDayColor(day);
            const isToday = isCurrentMonth && day === today;
            const isSelected = day === selectedDay;
            const hasTx = (txByDay[day]?.length || 0) > 0;
            const isPayDay2 = effectivePayDay && day === effectivePayDay;
            return (
              <button key={day} onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                style={{
                  aspectRatio:'1',
                  borderRadius:'var(--r-md)',
                  border: isSelected ? '2.5px solid var(--ink)' : isToday ? '2.5px solid #06B6D4' : isPayDay2 ? '2px solid #22C97A' : '1px solid var(--border)',
                  background: isToday ? '#06B6D4' : color ? color + '22' : isPayDay2 ? 'rgba(34,201,122,0.1)' : 'var(--surface)',
                  cursor: 'pointer',
                  display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                  padding:4, position:'relative', minHeight:42,
                }}>
                <span style={{ fontSize:14, fontWeight: isToday||isPayDay2 ? 800 : hasTx ? 700 : 400, color: isToday ? '#fff' : color ? color : 'var(--ink)' }}>
                  {day}
                </span>
                {isPayDay2 && !isToday && <span style={{ fontSize:8, marginTop:1 }}>💰</span>}
                {color && !isPayDay2 && <div style={{ width:6, height:6, borderRadius:'50%', background:color, marginTop:2 }}/>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day transactions */}
      {selectedDay && (
        <div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:10 }}>
            {selectedDay}. {getMonthNames(lang)[viewMonth]}
            {selectedTotal > 0 && <span style={{ fontSize:13, fontWeight:400, color:'var(--ink3)', marginLeft:8 }}>— {fmt(selectedTotal, lang)}</span>}
          </div>
          {selectedTxs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'1.5rem 0', color:'var(--ink3)', fontSize:13 }}>
              {lang==='en'?'No transactions this day':lang==='sv'?'Inga transaktioner denna dag':lang==='da'?'Ingen transaktioner denne dag':'Ingen transaksjoner denne dagen'}
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {selectedTxs.map(tx => (
                <div key={tx.id} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'10px 14px', display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ fontSize:20 }}>{tx.emoji || (tx.amount > 0 ? '💰' : '💸')}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:500, color:'var(--ink)' }}>{tx.note || tx.cat || (lang==='en'?'Transaction':lang==='sv'?'Transaktion':lang==='da'?'Transaktion':'Transaksjon')}</div>
                    <div style={{ fontSize:11, color:'var(--ink3)' }}>{tx.cat}</div>
                  </div>
                  <div style={{ fontSize:14, fontWeight:700, color: tx.amount > 0 ? '#22C97A' : 'var(--r400)' }}>
                    {tx.amount > 0 ? '+' : '-'}{fmt(tx.amount, lang)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!selectedDay && (state.transactions || []).length === 0 && (
        <div style={{ textAlign:'center', padding:'2rem 0', color:'var(--ink3)' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📅</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700 }}>
            {lang==='en'?'No transactions yet':lang==='sv'?'Inga transaktioner än':lang==='da'?'Ingen transaktioner endnu':'Ingen transaksjoner ennå'}
          </div>
          <div style={{ fontSize:13, marginTop:4 }}>
            {lang==='en'?'Add transactions to see your spending':lang==='sv'?'Lägg till transaktioner för att se dina utgifter':lang==='da'?'Tilføj transaktioner for at se dine udgifter':'Legg til transaksjoner for å se forbruket ditt'}
          </div>
        </div>
      )}

      <div style={{ height:16 }}/>
    </div>
  );
}
